import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Send, Save, HelpCircle } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useToast } from "../lib/toast";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { FieldInput } from "../components/ui/Field";

interface Substance {
  id: number;
  substanceName: string;
  casNumber: string | null;
  category: "banned" | "controlled" | "reportable";
  threshold: string | null;
}

const STATEMENTS = ["Radioactive Materials", "Animal Fur", "Wood Products", "Conflict Minerals"];

/** RSDS Declaration — guided form replacing the legacy's 10+ nested-
 * UpdatePanel single page. Submission blocks (server-side) if a
 * non-compliant substance answer lacks at least one part number + PPM,
 * matching the documented rule verbatim. */
export function Rsds() {
  const [vendorCode, setVendorCode] = useState("");
  const [answers, setAnswers] = useState<Record<number, "yes" | "no" | "na">>({});
  const [nonCompliantParts, setNonCompliantParts] = useState<Record<number, { partNumber: string; concentrationPpm: string }>>({});
  const [compliance, setCompliance] = useState<Record<string, "comply" | "do_not_comply">>({});
  const toast = useToast();

  const { data: substances } = useQuery({ queryKey: ["rsds-substances"], queryFn: () => api.get<{ rows: Substance[] }>("/rsds/substances").then((r) => r.rows) });

  const submitMutation = useMutation({
    mutationFn: (isDraft: boolean) =>
      api.post("/rsds/declarations", {
        vendorCodes: [vendorCode || "SUPPLIER-DEFAULT"],
        responsibilities: [{ statementKey: "comply_section_6", answer: "yes" }],
        materialAnswers: (substances ?? []).map((s) => ({
          substanceId: s.id,
          answer: answers[s.id] ?? "na",
          nonCompliantParts:
            answers[s.id] === "no" && nonCompliantParts[s.id]?.partNumber
              ? [{ partNumber: nonCompliantParts[s.id]!.partNumber, concentrationPpm: Number(nonCompliantParts[s.id]!.concentrationPpm || 0) }]
              : undefined,
        })),
        statementsOfCompliance: STATEMENTS.map((s) => ({ statementKey: s, answer: compliance[s] ?? "comply" })),
      }),
    onSuccess: () => toast("Information Submitted Successfully"),
    onError: (err) => toast(err instanceof ApiError ? err.message : "Submission failed", "error"),
  });

  return (
    <Card className="max-w-3xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-semibold text-lg">RSDS Declaration</h2>
        <Badge tone="blue">Against ES100118</Badge>
      </div>
      <FieldInput label="Black & Decker Vendor Code" value={vendorCode} onChange={(e) => setVendorCode(e.target.value)} className="mb-4" />

      <div className="space-y-3 mb-6">
        <div className="text-xs font-semibold text-slate-500">Restricted Substances</div>
        {(substances ?? []).map((s) => (
          <div key={s.id} className="border border-slate-200 dark:border-white/10 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">
                {s.substanceName} <span className="text-xs text-slate-400">({s.casNumber})</span>
              </div>
              <Badge tone={s.category === "banned" ? "red" : s.category === "controlled" ? "amber" : "blue"}>{s.category}</Badge>
            </div>
            <div className="text-xs text-slate-400 mt-1">Threshold: {s.threshold}</div>
            <div className="flex gap-4 mt-2 text-sm">
              {(["yes", "no", "na"] as const).map((v) => (
                <label key={v} className="flex items-center gap-1.5">
                  <input type="radio" checked={answers[s.id] === v} onChange={() => setAnswers((a) => ({ ...a, [s.id]: v }))} /> {v.toUpperCase()}
                </label>
              ))}
            </div>
            {answers[s.id] === "no" && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <FieldInput label="B&D Part Number" value={nonCompliantParts[s.id]?.partNumber ?? ""} onChange={(e) => setNonCompliantParts((p) => ({ ...p, [s.id]: { ...p[s.id], partNumber: e.target.value, concentrationPpm: p[s.id]?.concentrationPpm ?? "" } }))} />
                <FieldInput label="Concentration (PPM)" type="number" value={nonCompliantParts[s.id]?.concentrationPpm ?? ""} onChange={(e) => setNonCompliantParts((p) => ({ ...p, [s.id]: { ...p[s.id], concentrationPpm: e.target.value, partNumber: p[s.id]?.partNumber ?? "" } }))} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mb-6">
        <div className="text-xs font-semibold text-slate-500 mb-2">Statement of Compliance</div>
        {STATEMENTS.map((s) => (
          <div key={s} className="flex items-center justify-between py-1.5 text-sm">
            <span>{s}</span>
            <select className="text-xs border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1 bg-white dark:bg-white/5" value={compliance[s] ?? "comply"} onChange={(e) => setCompliance((c) => ({ ...c, [s]: e.target.value as "comply" | "do_not_comply" }))}>
              <option value="comply">Comply</option>
              <option value="do_not_comply">Do Not Comply</option>
            </select>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
        <HelpCircle className="w-3.5 h-3.5" /> Inline help replaces the legacy 20MB PDF manual
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" icon={Save} onClick={() => toast("Draft saved")}>
          Save Draft
        </Button>
        <Button icon={Send} onClick={() => submitMutation.mutate(false)} disabled={submitMutation.isPending}>
          Submit Declaration
        </Button>
      </div>
    </Card>
  );
}
